import React from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import { FormGroup, FormLabel, FormControl } from 'react-bootstrap';

import { cardImages } from '../itcgCardImages';
import { Deck } from '../game';

import { parseDeckListIntoTable } from './deck';

export enum Categories {
  LOG = 'log',
  LIST = 'list',
  EVENT = 'event',
  UPDATE = 'update',
  NEWS = 'news',
  GUIDE = 'guide',
}

const baseStyle: React.CSSProperties = {
  margin: '1%',
  display: 'flex',
};

const postPreviewStyle: React.CSSProperties = {
  background: 'linear-gradient(176deg, #DED4B3, 3%, #F5F2E8, 94%, #DED4B3)',
};

interface PostParams {
  id?: string;
}

interface PostProp {
  server: string;
  username: string;
}

interface DecklistState {
  deckName?: string;
  deckList?: Deck;
  owner?: string;
}

interface PostState {
  id?: string;
  title: string;
  body: string;
  category: Categories;
}

interface WarningState {
  warning?: string;
}

const DEFAULT_BODY = `
## Post example
Include additional **descriptions** here, *how to play* guides for your deck, or just generally ***helpful information***.

### Note
1. This uses Markdown for formatting
1. See the link below for more information
`;

export function ITCGWritePosts(props: PostProp) {
  const { id } = useParams<PostParams>();
  const history = useHistory();
  const [warning, setWarning] = React.useState<WarningState>({});
  const [deck, setDeck] = React.useState<DecklistState>({});

  const [post, setPost] = React.useState<PostState>({
    title: deck?.deckName ?? 'Default Post Title',
    body: DEFAULT_BODY,
    category: Categories.LOG,
  });

  React.useEffect(() => {
    (async () => {
      if (!id) return;

      const postResp = await axios(`/posts/${id}`, {
        baseURL: props.server,
        timeout: 5000,
        withCredentials: true,
      }).catch(() => {
        return;
      });

      const deckResp = await axios(`/decks/${id}`, {
        baseURL: props.server,
        timeout: 5000,
        withCredentials: true,
      }).catch(() => {
        return;
      });

      if (!deckResp?.data && !postResp?.data) return;
      if (deckResp?.data) {
        setDeck({
          ...deck,
          deckList: deckResp.data.deck_list,
          deckName: deckResp.data.name,
          owner: deckResp.data.owner.username,
        });
        setPost({
          ...post,
          title: deckResp.data.name,
          category: Categories.LIST,
        });
      } else if (postResp?.data) {
        setDeck({
          ...deck,
          deckList: postResp.data.post.postdecklist?.deck_list,
          owner: postResp.data.post.owner.username,
        });
        setPost({
          ...post,
          id: postResp.data.post.id,
          title: postResp.data.post.title,
          body: postResp.data.post.body,
          category: postResp.data.post.category,
        });
      }
    })();
  }, []);

  const submitPost = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const resp = await axios
      .post(
        '/posts/upsert',
        {
          postId: post.id,
          postTitle: post.title,
          postCategory: post.category,
          postBody: post.body,
          postDecklist: deck.deckList,
        },
        {
          baseURL: props.server,
          timeout: 5000,
          withCredentials: true,
        },
      )
      .catch((error) => {
        if (error.response) return setWarning({ warning: error.response.data });
        if (error) setWarning({ warning: error.toString() });
      });

    if (resp) history.push(`/posts/${resp.data.id}`);
  };

  return (
    <div className="flex row" style={baseStyle}>
      {id && !post.id && !deck ? (
        <>
          <div className="border border-3 sm-8 col" style={postPreviewStyle}>
            <h1>Deck does not exist</h1>
          </div>
          <div>
            <a href={'/decks'} className="paper-btn btn-warning margin">
              Ok
            </a>
          </div>
        </>
      ) : (
        <>
          <div className="sm-8 col">
            <h4 className="margin-none padding-none" style={{ textAlign: 'center' }}>
              Preview
            </h4>
            <div className="border border-3 padding" style={postPreviewStyle}>
              <h1 style={{ textAlign: 'center' }} className="margin-bottom">
                {post.title}
              </h1>
              {!!deck?.deckList && (
                <>
                  <h3 style={{ textAlign: 'center' }} className="margin-top">
                    by {deck.owner}
                  </h3>
                  <div className="row">
                    <div className="sm-5 padding-top">
                      <img
                        className="no-border"
                        src={cardImages[deck.deckList.character.image].top}
                      ></img>
                      <img
                        className="no-border"
                        src={cardImages[deck.deckList.character.image].skill}
                      ></img>
                      <img
                        className="no-border"
                        src={cardImages[deck.deckList.character.image].skill2}
                      ></img>
                      <img
                        className="no-border"
                        src={cardImages[deck.deckList.character.image].skill3}
                      ></img>
                    </div>
                    <div className="sm-7 col">
                      {parseDeckListIntoTable(deck.deckList)}
                    </div>
                  </div>
                </>
              )}
              <Markdown>{post.body}</Markdown>
            </div>
          </div>
          <form className="sm-4 col" onSubmit={async (e) => await submitPost(e)}>
            <FormLabel>Post Title (max length: 30)</FormLabel>
            <FormGroup controlId={'header'}>
              <FormControl
                type={'text'}
                value={post.title}
                className="shadow shadow-small"
                onChange={(e) => setPost({ ...post, title: e.target.value })}
              />
            </FormGroup>
            <FormLabel>Post Body</FormLabel>
            <FormGroup controlId={'body'}>
              <FormControl
                as={'textarea'}
                className="shadow shadow-small"
                rows={20}
                style={{ width: '100%' }}
                value={post.body}
                onChange={(e) => setPost({ ...post, body: e.target.value })}
              />
            </FormGroup>
            {!deck?.deckList && (
              <FormGroup controlId={'category'}>
                <FormLabel>Category</FormLabel>
                <FormControl
                  as={'select'}
                  className="shadow shadow-small"
                  value={post.category}
                  onChange={async (e) =>
                    setPost({
                      ...post,
                      category: Categories[e.target.value.toUpperCase()],
                    })
                  }
                >
                  {Object.values(Categories).map((k) => {
                    return <option>{k}</option>;
                  })}
                </FormControl>
              </FormGroup>
            )}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={'https://www.markdownguide.org/cheat-sheet/'}
            >
              Markdown Cheatsheet
            </a>
            {!!deck?.deckList && (
              <div className="padding-left padding-right margin-top margin-bottom border border-5 border-warning shadow">
                <p>
                  You cannot edit decklists in posts. Share new versions of a deck in a
                  new post.
                </p>
              </div>
            )}
            <div className="padding margin-top flex-spaces row border border-2 shadow">
              <button className="btn-secondary-outline" type={'submit'}>
                Publish
              </button>
              <label className="paper-btn btn-danger-outline" htmlFor="modal-1">
                Discard
              </label>
              <input className="modal-state" id="modal-1" type="checkbox" />
              <div className="modal">
                <label className="modal-bg" htmlFor="modal-1"></label>
                <div className="modal-body">
                  <label className="btn-close" htmlFor="modal-1">
                    X
                  </label>
                  <h4 className="modal-title">Discard Changes</h4>
                  <p className="modal-text">
                    Are you sure you want to discard changes to this post?
                  </p>
                  <a
                    href={
                      !post.id
                        ? !!deck.deckList
                          ? '/decks'
                          : '/posts'
                        : `/posts/${post.id}`
                    }
                    className="paper-btn btn-danger"
                  >
                    Yes
                  </a>
                </div>
              </div>
            </div>
            <div className="text-danger">{warning.warning}</div>
          </form>
        </>
      )}
    </div>
  );
}
